<template>
  <div class="start-journey">
    <div class="start-journey__container">
      <div class="start-journey__container__content">
        <SimpleLandingLogo :corporate="corporate" />
        <img
          :alt="$t('b2b__landing_page__start_journey_us__title')"
          class="start-journey__container__image mobile"
          :src="image"
        />
        <h2 class="kds-typography-display-small">
          {{ title }}
        </h2>
        <p
          class="kds-typography-paragraph-large"
          v-html="description"
        />
        <div
          class="button-wrap"
          :class="{ show_scroll: showBottomShadow }"
        >
          <BaseButton
            ref="ctaButton"
            class="start-journey__container__cta"
            data-qa="submit"
            @click="handleCTAButtonClick"
          >
            {{ ctaBtnTitle }}
            <template #append>
              <ArrowRightIcon />
            </template>
          </BaseButton>
        </div>
        <div ref="stickyButtonProbeRef"></div>
      </div>
      <img
        :alt="$t('b2b__landing_page__start_journey_us__title')"
        class="start-journey__container__image desktop"
        :src="image"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import BaseButton from '@shared/components/BaseButton.vue'
import SimpleLandingLogo from '@us/app/components/SimpleLandingLogo.vue'
import ArrowRightIcon from '@shared/assets/icon/ArrowRightIcon.vue'
import imageMskEn from '@shared/assets/img/landing-page-main-img-en-US.jpg'
import imageMskEs from '@shared/assets/img/landing-page-main-img-es-US.jpg'
import { useI18n } from 'petite-vue-i18n'

const props = defineProps({
  ctaBtnTitle: {
    type: String,
    default: '',
  },
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  receiveCTAButtonClientRect: {
    type: Function,
    default: () => {},
  },
  variant: {
    type: String,
    default: '',
  },
})

const userStore = useUserStore()
const corporateStore = useCorporateStore()
const { t } = useI18n()

const ctaButton = ref()
const corporate = computed(() => corporateStore.corporate)
const lang = computed(() => userStore.lang)
const image = computed(() => {
  if (lang.value === 'es-US') return imageMskEs
  return imageMskEn
})
const title = computed(() => {
  switch (props.variant) {
    case 'women':
      return t('b2b__landing_page__start_journey_us_women__title')
    default:
      return t('b2b__landing_page__start_journey_us__title')
  }
})
const description = computed(() => {
  switch (props.variant) {
    case 'women':
      return t('b2b__landing_page__start_journey_us_women__description')
    default:
      return t('b2b__landing_page__start_journey_us__description')
  }
})

const showBottomShadow = ref(false)
const stickyButtonProbeRef = ref(null)
const observerRef = ref(null)

onMounted(() => {
  props.receiveCTAButtonClientRect(() =>
    ctaButton?.value?.button?.getBoundingClientRect(),
  )

  observerRef.value = new IntersectionObserver(
    ([entry]) => (showBottomShadow.value = !entry.isIntersecting),
    { root: null, threshold: 0 },
  )
  if (stickyButtonProbeRef.value) {
    observerRef.value.observe(stickyButtonProbeRef.value)
  }
})

onBeforeUnmount(() => {
  if (observerRef.value) observerRef.value.disconnect()
})
</script>

<style lang="scss" scoped>
.start-journey {
  padding: 10px;

  @include media-up(lg) {
    padding: 20px 10px;
  }

  @include media-up(xxl) {
    display: flex;
    padding: 60px 0;
  }

  &__container {
    @include flex-container(column);

    max-width: 1200px;
    margin: auto;

    @include media-up(md) {
      @include flex-container(row, space-between);
      grid-gap: $kds-space-2x;
    }

    @include media-up(lg) {
      grid-gap: $kds-space-8x;
    }

    &__content {
      @include flex-container(column, flex-start, md, start);

      @include media-up(md) {
        @include flex-container(column, flex-start, lg, start);
        max-width: 768px;
        margin: auto;
      }

      @include media-up(lg) {
        @include flex-container(column, center, lg);
      }
    }

    .button-wrap {
      width: 100%;
      position: sticky;
      bottom: 0;
      padding: 10px;
      margin: -10px;
      background-color: white;

      &.show_scroll {
        box-shadow: 0 -2px 2px rgba(10, 28, 64, 0.1);
      }
    }

    &__cta {
      @include media-up(md) {
        width: fit-content;
      }

      @include media-up(lg) {
        margin: 0;
      }
    }

    &__image {
      width: 80%;
      margin: auto;
      object-fit: contain;
      max-height: 327px;
      align-self: center;

      @include media-up(lg) {
        width: 590px;
        max-height: 492px;
        margin: auto;

        @media (max-height: 700px) {
          max-height: 430px;
        }
      }

      &.mobile {
        @include media-up(md) {
          display: none;
        }
      }

      &.desktop {
        display: none;

        @include media-up(md) {
          display: initial;
          align-self: center;
        }
      }
    }
  }
}
</style>
